import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Users,
  DollarSign,
  TrendingUp,
  Activity,
  Store,
  RefreshCw,
  Eye,
  Send,
  BarChart3,
  Shield,
  Pencil,
  Trash2,
  Plus,
  AlertTriangle,
  Database,
  ArrowRight,
  Building2,
  CreditCard,
  Wallet,
  PiggyBank
} from 'lucide-react';
import { toast } from 'sonner';
import { LiveMarketAdminCard } from '../components/sblive/LiveMarketAdminCard';
import { BusinessOwnersCard } from '../components/sblive/BusinessOwnersCard';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [allUsers, setAllUsers] = useState({ users: [], total: 0 });
  const [allBusinesses, setAllBusinesses] = useState({ businesses: [], total: 0 });
  const [businessInvestments, setBusinessInvestments] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [migratingAll, setMigratingAll] = useState(false);
  const [automation, setAutomation] = useState({ enabled: true, threshold: 25, cadence: 'daily' });
  const [automationRuns, setAutomationRuns] = useState([]);
  
  // User modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  
  // Business modals
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [deletingBusiness, setDeletingBusiness] = useState(null);
  const [creatingBusiness, setCreatingBusiness] = useState(false);
  const [newBusiness, setNewBusiness] = useState({
    name: '', description: '', category: '', owner_name: '', location: '',
    investment_goal: '', min_investment: '20', expected_return: '', image_url: '', website: '', featured: false
  });
  
  // Payout
  const [payoutBusiness, setPayoutBusiness] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [processingPayout, setProcessingPayout] = useState(false);
  
  // Sraivest Banking
  const [sravestBanking, setSravestBanking] = useState(null);
  const [sravestRevenue, setSravestRevenue] = useState(null);
  const [showBankingForm, setShowBankingForm] = useState(false);
  const [bankingForm, setBankingForm] = useState({
    bank_name: '',
    account_holder_name: 'Marcus Dupree',
    routing_number: '',
    account_number: '',
    account_type: 'checking'
  });
  const [withdrawAmount, setWithdrawAmount] = useState('');
  
  // Payment Verification (Cash App / Venmo)
  const [pendingPayments, setPendingPayments] = useState([]);
  const [allTransactions, setAllTransactions] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchDashboard();
    fetchAutomation();
  }, []);

  const fetchAutomation = async () => {
    try {
      const res = await api.get('/sb-live/admin/automation');
      setAutomation(res.data.settings);
      setAutomationRuns(res.data.runs);
    } catch (e) {
      console.error('Failed to load payout automation', e);
    }
  };

  const saveAutomation = async () => {
    try {
      const res = await api.put('/sb-live/admin/automation', automation);
      setAutomation(res.data.settings);
      toast.success('Payout automation saved');
    } catch (e) {
      toast.error('Failed to save payout automation');
    }
  };

  const runAutomationNow = async () => {
    try {
      await api.post('/sb-live/admin/run-automation-payouts');
      toast.success('Automation run complete');
      fetchAutomation();
    } catch (e) {
      toast.error('Failed to run payout automation');
    }
  };

  const fetchDashboard = async () => {
    try {
      const [dashRes, usersRes, bizRes, investRes, analyticsRes, bankingRes, revenueRes, pendingRes, transactionsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/users'),
        api.get('/admin/businesses'),
        api.get('/admin/business-investments'),
        api.get('/admin/analytics'),
        api.get('/admin/sraivest-banking').catch(() => ({ data: { is_configured: false } })),
        api.get('/admin/sraivest-revenue').catch(() => ({ data: { total_revenue: 0, breakdown: {} } })),
        api.get('/payments/admin/pending').catch(() => ({ data: { transactions: [], pending_count: 0 } })),
        api.get('/payments/admin/all-transactions').catch(() => ({ data: { transactions: [], stats: {} } }))
      ]);
      setDashboard(dashRes.data);
      setAllUsers(usersRes.data);
      setAllBusinesses(bizRes.data);
      setBusinessInvestments(investRes.data);
      setAnalytics(analyticsRes.data);
      setSravestBanking(bankingRes.data);
      setSravestRevenue(revenueRes.data);
      setPendingPayments(pendingRes.data.transactions || []);
      setAllTransactions(transactionsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load admin dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const res = await api.get(`/admin/users/${userId}`);
      setSelectedUser(res.data);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  // User CRUD handlers
  const handleUpdateUser = async () => {
    try {
      const updateData = {};
      if (editingUser.name) updateData.name = editingUser.name;
      if (editingUser.email) updateData.email = editingUser.email;
      if (editingUser.balance !== undefined) updateData.balance = parseFloat(editingUser.balance);
      
      await api.put(`/admin/users/${editingUser.user_id}`, updateData);
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${deletingUser.user_id}`);
      toast.success('User deleted successfully');
      setDeletingUser(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Business CRUD handlers
  const handleCreateBusiness = async () => {
    try {
      const businessData = {
        ...newBusiness,
        investment_goal: parseFloat(newBusiness.investment_goal),
        min_investment: parseFloat(newBusiness.min_investment),
        expected_return: parseFloat(newBusiness.expected_return)
      };
      await api.post('/admin/businesses', businessData);
      toast.success('Business created successfully');
      setCreatingBusiness(false);
      setNewBusiness({
        name: '', description: '', category: '', owner_name: '', location: '',
        investment_goal: '', min_investment: '20', expected_return: '', image_url: '', website: '', featured: false
      });
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create business');
    }
  };

  const handleUpdateBusiness = async () => {
    try {
      const updateData = {};
      if (editingBusiness.name) updateData.name = editingBusiness.name;
      if (editingBusiness.description) updateData.description = editingBusiness.description;
      if (editingBusiness.category) updateData.category = editingBusiness.category;
      if (editingBusiness.status) updateData.status = editingBusiness.status;
      if (editingBusiness.expected_return) updateData.expected_return = parseFloat(editingBusiness.expected_return);
      
      await api.put(`/admin/businesses/${editingBusiness.business_id}`, updateData);
      toast.success('Business updated successfully');
      setEditingBusiness(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update business');
    }
  };

  const handleDeleteBusiness = async () => {
    try {
      await api.delete(`/admin/businesses/${deletingBusiness.business_id}`);
      toast.success('Business deleted successfully');
      setDeletingBusiness(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete business');
    }
  };

  const handleMigrateBusiness = async (businessId) => {
    try {
      await api.post(`/admin/businesses/${businessId}/migrate`);
      toast.success('Business migrated to database');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to migrate business');
    }
  };

  const handleMigrateAll = async () => {
    setMigratingAll(true);
    try {
      const res = await api.post('/admin/businesses/migrate-all');
      toast.success(res.data.message);
      if (res.data.migrated.length > 0) {
        toast.info(`Migrated: ${res.data.migrated.map(b => b.name).join(', ')}`);
      }
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to migrate businesses');
    } finally {
      setMigratingAll(false);
    }
  };

  // Payout handler
  const processPayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      toast.error('Enter a valid payout amount');
      return;
    }

    setProcessingPayout(true);
    try {
      const res = await api.post(`/admin/process-payout/${payoutBusiness.business_id}?payout_amount=${payoutAmount}`);
      toast.success(`Payout processed! Distributed to ${res.data.payouts.length} investors`);
      setPayoutBusiness(null);
      setPayoutAmount('');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout failed');
    } finally {
      setProcessingPayout(false);
    }
  };

  // Payment Verification Handlers (Cash App / Venmo)
  const handleApprovePayment = async (transactionId) => {
    try {
      const res = await api.post('/payments/admin/verify', {
        transaction_id: transactionId,
        action: 'approve',
        admin_notes: adminNotes || 'Payment verified and approved'
      });
      toast.success(res.data.message || 'Payment approved!');
      setVerifyingPayment(null);
      setAdminNotes('');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payment');
    }
  };

  const handleRejectPayment = async (transactionId) => {
    if (!adminNotes) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      const res = await api.post('/payments/admin/verify', {
        transaction_id: transactionId,
        action: 'reject',
        admin_notes: adminNotes
      });
      toast.success('Payment rejected');
      setVerifyingPayment(null);
      setAdminNotes('');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject payment');
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value?.toLocaleString() || 0}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!dashboard) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Admin Access Required</h2>
          <p className="text-gray-400">You don't have permission to view this page.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-gray-400 mt-1">Platform management and analytics</p>
          </div>
          <Button onClick={fetchDashboard} className="btn-secondary gap-2" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>

        <LiveMarketAdminCard />
        <BusinessOwnersCard />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total Users</span>
                <Users className="w-5 h-5 text-[#6366F1]" />
              </div>
              <p className="text-2xl font-bold text-white number" data-testid="total-users">{dashboard.stats.total_users}</p>
            </CardContent>
          </Card>
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total Volume</span>
                <DollarSign className="w-5 h-5 text-[#00E676]" />
              </div>
              <p className="text-2xl font-bold text-[#00E676] number">{formatCurrency(dashboard.stats.total_volume)}</p>
            </CardContent>
          </Card>
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Transactions</span>
                <Activity className="w-5 h-5 text-[#F7931A]" />
              </div>
              <p className="text-2xl font-bold text-white number">{dashboard.stats.total_transactions}</p>
            </CardContent>
          </Card>
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Business Investments</span>
                <Store className="w-5 h-5 text-[#2962FF]" />
              </div>
              <p className="text-2xl font-bold text-white number">{dashboard.stats.total_business_investments}</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 p-1 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#6366F1] data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-[#00D632] data-[state=active]:text-black" data-testid="payments-tab">
              <CreditCard className="w-4 h-4 mr-2" /> Payments
              {pendingPayments.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingPayments.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#6366F1] data-[state=active]:text-white" data-testid="users-tab">
              <Users className="w-4 h-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="businesses" className="data-[state=active]:bg-[#6366F1] data-[state=active]:text-white" data-testid="businesses-tab">
              <Store className="w-4 h-4 mr-2" /> Businesses
            </TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-[#6366F1] data-[state=active]:text-white">
              <Send className="w-4 h-4 mr-2" /> Payouts
            </TabsTrigger>
            <TabsTrigger value="banking" className="data-[state=active]:bg-[#00E676] data-[state=active]:text-black" data-testid="banking-tab">
              <Building2 className="w-4 h-4 mr-2" /> Sraivest Banking
            </TabsTrigger>
          </TabsList>

          {/* Payments Verification Tab */}
          <TabsContent value="payments" className="mt-6">
            <div className="space-y-6">
              {/* Pending Payments Alert */}
              {pendingPayments.length > 0 && (
                <Card className="border-yellow-500 bg-yellow-500/10">
                  <CardContent className="p-4 flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-yellow-500" />
                    <div>
                      <p className="font-bold text-white">{pendingPayments.length} Pending Verification{pendingPayments.length > 1 ? 's' : ''}</p>
                      <p className="text-sm text-gray-400">Cash App and Venmo deposits waiting for approval</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Pending Payments List */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#00D632]" />
                    Pending Payment Verifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pendingPayments.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No pending payments to verify</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingPayments.map((payment) => (
                        <div key={payment.transaction_id} className="bg-white/5 rounded-lg p-4 border border-white/10">
                          <div className="flex items-start justify-between">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  payment.method === 'cashapp' ? 'bg-[#00D632]/20 text-[#00D632]' : 'bg-[#3D95CE]/20 text-[#3D95CE]'
                                }`}>
                                  {payment.method === 'cashapp' ? 'Cash App' : 'Venmo'}
                                </span>
                                <span className="text-gray-400 text-sm">{payment.transaction_id}</span>
                              </div>
                              <p className="text-white font-medium">{payment.user_email}</p>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-400">Amount:</span>
                                  <span className="text-white ml-2 font-bold">${payment.gross_amount}</span>
                                </div>
                                <div>
                                  <span className="text-gray-400">Net:</span>
                                  <span className="text-[#00E676] ml-2 font-bold">${payment.net_amount}</span>
                                </div>
                                <div>
                                  <span className="text-gray-400">
                                    {payment.method === 'cashapp' ? 'Cashtag:' : 'Username:'}
                                  </span>
                                  <span className="text-white ml-2">
                                    {payment.cashtag || payment.venmo_username}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-400">For:</span>
                                  <span className="text-white ml-2 capitalize">{payment.investment_type}</span>
                                </div>
                              </div>
                              <p className="text-gray-500 text-xs">
                                Created: {new Date(payment.created_at).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button 
                                size="sm" 
                                className="bg-[#00E676] text-black hover:bg-[#00E676]/80"
                                onClick={() => handleApprovePayment(payment.transaction_id)}
                                data-testid={`approve-${payment.transaction_id}`}
                              >
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-red-500 text-red-500 hover:bg-red-500/20"
                                onClick={() => setVerifyingPayment(payment)}
                                data-testid={`reject-${payment.transaction_id}`}
                              >
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* All Transactions */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    All Transactions
                    {allTransactions?.stats && (
                      <span className="text-sm font-normal text-gray-400 ml-2">
                        ({allTransactions.stats.total || 0} total)
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {allTransactions?.stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <p className="text-yellow-500 text-2xl font-bold">{allTransactions.stats.pending || 0}</p>
                        <p className="text-gray-400 text-sm">Pending</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <p className="text-blue-500 text-2xl font-bold">{allTransactions.stats.processing || 0}</p>
                        <p className="text-gray-400 text-sm">Processing</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <p className="text-[#00E676] text-2xl font-bold">{allTransactions.stats.completed || 0}</p>
                        <p className="text-gray-400 text-sm">Completed</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 text-center">
                        <p className="text-red-500 text-2xl font-bold">{allTransactions.stats.failed || 0}</p>
                        <p className="text-gray-400 text-sm">Failed</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 text-gray-400">ID</th>
                          <th className="text-left py-2 text-gray-400">User</th>
                          <th className="text-left py-2 text-gray-400">Method</th>
                          <th className="text-left py-2 text-gray-400">Type</th>
                          <th className="text-right py-2 text-gray-400">Amount</th>
                          <th className="text-center py-2 text-gray-400">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(allTransactions?.transactions || []).slice(0, 20).map((tx) => (
                          <tr key={tx.transaction_id} className="border-b border-white/5">
                            <td className="py-2 text-gray-400 font-mono text-xs">{tx.transaction_id?.slice(0, 12)}...</td>
                            <td className="py-2 text-white">{tx.user_email}</td>
                            <td className="py-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                tx.method === 'paypal' ? 'bg-blue-500/20 text-blue-400' :
                                tx.method === 'cashapp' ? 'bg-[#00D632]/20 text-[#00D632]' :
                                'bg-[#3D95CE]/20 text-[#3D95CE]'
                              }`}>
                                {tx.method}
                              </span>
                            </td>
                            <td className="py-2 text-white capitalize">{tx.type}</td>
                            <td className="py-2 text-right text-white font-medium">${tx.gross_amount?.toFixed(2)}</td>
                            <td className="py-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                tx.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                tx.status === 'pending_verification' ? 'bg-yellow-500/20 text-yellow-400' :
                                tx.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {tx.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Volume Chart */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Daily Transaction Volume</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics?.daily_volume || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="_id" stroke="#666" />
                        <YAxis stroke="#666" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid #333' }}
                          formatter={(value) => [`$${value.toLocaleString()}`, 'Volume']}
                        />
                        <Bar dataKey="volume" fill="#00E676" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Users */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Signups</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {(dashboard?.recent_users || []).map((u, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                            {u.picture ? (
                              <img src={u.picture} alt={u.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <Users className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-white">{u.name}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-[#00E676] number">{formatCurrency(u.balance)}</p>
                          <p className="text-xs text-gray-500">{new Date(u.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  All Users ({allUsers.total})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">User</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Balance</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">2FA</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(allUsers?.users || allUsers || []).map((u, i) => (
                        <tr key={i} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${u.user_id}`}>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                                {u.picture ? (
                                  <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                                ) : (
                                  <Users className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                              <span className="text-white">{u.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-400">{u.email}</td>
                          <td className="py-3 px-4 text-right text-[#00E676] number">{formatCurrency(u.balance)}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${u.two_fa_enabled ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-white/10 text-gray-500'}`}>
                              {u.two_fa_enabled ? 'Enabled' : 'Off'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => fetchUserDetails(u.user_id)} className="text-gray-400 hover:text-white" data-testid={`view-user-${u.user_id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setEditingUser(u)} className="text-gray-400 hover:text-blue-400" data-testid={`edit-user-${u.user_id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeletingUser(u)} className="text-gray-400 hover:text-red-400" data-testid={`delete-user-${u.user_id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Businesses Tab */}
          <TabsContent value="businesses" className="mt-6">
            <Card className="card-dashboard">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  All Businesses ({allBusinesses.total})
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button onClick={handleMigrateAll} disabled={migratingAll} className="btn-secondary gap-2" data-testid="migrate-all-btn">
                    {migratingAll ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Database className="w-4 h-4" />
                    )}
                    Migrate All to DB
                  </Button>
                  <Button onClick={() => setCreatingBusiness(true)} className="btn-primary gap-2" data-testid="create-business-btn">
                    <Plus className="w-4 h-4" /> Add Business
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(allBusinesses?.businesses || allBusinesses || []).map((biz, i) => (
                    <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10" data-testid={`business-row-${biz.business_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden">
                            <img src={biz.image_url} alt={biz.name} className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=200'; }} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-white">{biz.name}</h3>
                              {biz.featured && <span className="px-2 py-0.5 rounded-full text-xs bg-[#6366F1]/20 text-[#6366F1]">Featured</span>}
                              <span className={`px-2 py-0.5 rounded-full text-xs ${biz.status === 'active' ? 'bg-[#00E676]/20 text-[#00E676]' : biz.status === 'funded' ? 'bg-[#6366F1]/20 text-[#6366F1]' : 'bg-gray-500/20 text-gray-400'}`}>
                                {biz.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-400">{biz.category} • {biz.location}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xl font-bold text-[#00E676] number">{formatCurrency(biz.current_investment)}</p>
                            <p className="text-sm text-gray-400">of {formatCurrency(biz.investment_goal)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setEditingBusiness(biz)} className="text-gray-400 hover:text-blue-400" data-testid={`edit-business-${biz.business_id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeletingBusiness(biz)} className="text-gray-400 hover:text-red-400" data-testid={`delete-business-${biz.business_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Payouts Tab */}
          <TabsContent value="payouts" className="mt-6">
    <Card className="card-dashboard">
      <CardHeader>
        <CardTitle>Payout Automation</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={automation.enabled} onChange={(e) => setAutomation({ ...automation, enabled: e.target.checked })} />
            Automation on
          </label>
          <label className="flex items-center gap-2">
            Threshold ($)
            <Input type="number" min="0" value={automation.threshold} onChange={(e) => setAutomation({ ...automation, threshold: Number(e.target.value) })} className="w-24" />
          </label>
          <label className="flex items-center gap-2">
            Cadence
            <select value={automation.cadence} onChange={(e) => setAutomation({ ...automation, cadence: e.target.value })}>
              <option value="daily">daily</option>
              <option value="weekly">weekly</option>
            </select>
          </label>
        <Button onClick={saveAutomation}>Save</Button>
        <Button onClick={runAutomationNow}>Run now</Button>
        </div>
        <p className="text-sm text-gray-400 mt-2">Daily runs pay businesses at or above the threshold with complete payout details through the existing payout path, then credit their investors pro-rata. Businesses missing payout details are skipped and flagged below.</p>
        <div className="mt-4 space-y-2">
          {automationRuns.map((run, i) => (
            <div key={i} className="border rounded p-2">
              <div>{run.started_at} - {run.status} - threshold ${run.threshold} - sent {run.payouts_sent}, skipped {run.payouts_skipped}, distributed ${run.distributed_total}</div>
                {run.payouts.map((p, j) => (
                  <div key={j}>{p.business_id}: {p.status}{p.net!== undefined? ` - net ${p.net}`: ''}{p.reason? ` - ${p.reason}`: ''}{p.distributed!== undefined? ` - distributed ${p.distributed}`: ''}</div>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Business Investments - Process Payouts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(businessInvestments || []).map((biz, i) => (
                    <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-white">{biz.business_name}</h3>
                          <p className="text-sm text-gray-400">ID: {biz.business_id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#00E676] number">{formatCurrency(biz.total_invested)}</p>
                          <p className="text-sm text-gray-400">{biz.investor_count} investors</p>
                        </div>
                        <Button className="btn-primary" onClick={() => setPayoutBusiness(biz)} data-testid={`payout-${biz.business_id}`}>
                          <Send className="w-4 h-4 mr-2" /> Process Payout
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(!businessInvestments?.businesses || businessInvestments.businesses.length === 0) && (
                    <div className="text-center py-12">
                      <Store className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No business investments to process</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sraivest Banking Tab */}
          <TabsContent value="banking" className="mt-6">
            <div className="space-y-6">
              {/* Revenue Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="card-dashboard border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/10 to-transparent">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Total Revenue</p>
                        <p className="text-2xl font-bold text-[#00E676]">${sravestRevenue?.total_revenue?.toFixed(2) || '0.00'}</p>
                      </div>
                      <PiggyBank className="w-8 h-8 text-[#00E676]" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-dashboard">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Investment Fees (5%)</p>
                        <p className="text-2xl font-bold text-white">${sravestRevenue?.breakdown?.investment_fees?.toFixed(2) || '0.00'}</p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-dashboard">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Withdrawal Fees</p>
                        <p className="text-2xl font-bold text-white">${sravestRevenue?.breakdown?.withdrawal_fees?.toFixed(2) || '0.00'}</p>
                      </div>
                      <Wallet className="w-8 h-8 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-dashboard">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Business Cashout Fees</p>
                        <p className="text-2xl font-bold text-white">${sravestRevenue?.breakdown?.business_cashout_fees?.toFixed(2) || '0.00'}</p>
                      </div>
                      <Store className="w-8 h-8 text-orange-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Banking Setup */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#00E676]" />
                    Sraivest Company Bank Account
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sravestBanking?.is_configured ? (
                    <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-[#00E676]/30">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#00E676]/20 flex items-center justify-center">
                          <CreditCard className="w-6 h-6 text-[#00E676]" />
                        </div>
                        <div>
                          <p className="text-white font-semibold">{sravestBanking.bank_name}</p>
                          <p className="text-gray-400">****{sravestBanking.account_last4}</p>
                          <p className="text-xs text-gray-500">Account Holder: {sravestBanking.account_holder_name}</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="border-white/10"
                        onClick={() => setShowBankingForm(true)}
                      >
                        Update
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <AlertTriangle className="w-12 h-12 mx-auto text-[#F7931A] mb-4" />
                      <p className="text-white font-semibold mb-2">Banking Not Configured</p>
                      <p className="text-gray-400 mb-4">Set up your bank account to receive platform revenue</p>
                      <Button 
                        className="bg-[#00E676] text-black hover:bg-[#00E676]/80"
                        onClick={() => setShowBankingForm(true)}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Set Up Banking
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Withdraw Revenue */}
              {sravestBanking?.is_configured && sravestRevenue?.total_revenue > 0 && (
                <Card className="card-dashboard">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#F7931A]" />
                      Withdraw Platform Revenue
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex-1">
                        <Label className="text-gray-400">Amount to Withdraw</Label>
                        <div className="relative mt-1">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <Input
                            type="number"
                            className="input-dark pl-10"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            placeholder="Enter amount (min $100)"
                            min="100"
                            max={sravestRevenue?.total_revenue || 0}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Available: ${sravestRevenue?.total_revenue?.toFixed(2) || '0.00'}</p>
                      </div>
                      <Button
                        className="bg-[#F7931A] text-black hover:bg-[#F7931A]/80 h-12 mt-auto"
                        disabled={!withdrawAmount || parseFloat(withdrawAmount) < 100}
                        onClick={async () => {
                          try {
                            const res = await api.post(`/admin/sraivest-withdraw?amount=${withdrawAmount}`);
                            toast.success(res.data.message);
                            setWithdrawAmount('');
                            fetchDashboard();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Withdrawal failed');
                          }
                        }}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Withdraw to Bank
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Owner Earnings Overview */}
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white">Business Owner Earnings Paid</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-purple-400">${sravestRevenue?.owner_earnings_paid?.toFixed(2) || '0.00'}</p>
                  <p className="text-sm text-gray-400 mt-1">Total 90% share paid to business owners</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Banking Setup Dialog */}
        <Dialog open={showBankingForm} onOpenChange={setShowBankingForm}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>Sraivest Banking Information</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-gray-400">Bank Name</Label>
                <Input
                  className="input-dark mt-1"
                  value={bankingForm.bank_name}
                  onChange={(e) => setBankingForm({...bankingForm, bank_name: e.target.value})}
                  placeholder="Chase, Bank of America, etc."
                />
              </div>
              <div>
                <Label className="text-gray-400">Account Holder Name</Label>
                <Input
                  className="input-dark mt-1"
                  value={bankingForm.account_holder_name}
                  onChange={(e) => setBankingForm({...bankingForm, account_holder_name: e.target.value})}
                  placeholder="Marcus Dupree"
                />
              </div>
              <div>
                <Label className="text-gray-400">Routing Number</Label>
                <Input
                  className="input-dark mt-1"
                  value={bankingForm.routing_number}
                  onChange={(e) => setBankingForm({...bankingForm, routing_number: e.target.value})}
                  placeholder="9-digit routing number"
                  maxLength={9}
                />
              </div>
              <div>
                <Label className="text-gray-400">Account Number</Label>
                <Input
                  className="input-dark mt-1"
                  value={bankingForm.account_number}
                  onChange={(e) => setBankingForm({...bankingForm, account_number: e.target.value})}
                  placeholder="Account number"
                />
              </div>
              <div>
                <Label className="text-gray-400">Account Type</Label>
                <select
                  className="w-full mt-1 p-2 rounded-lg bg-white/5 border border-white/10 text-white"
                  value={bankingForm.account_type}
                  onChange={(e) => setBankingForm({...bankingForm, account_type: e.target.value})}
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-white/10" onClick={() => setShowBankingForm(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-[#00E676] text-black hover:bg-[#00E676]/80"
                onClick={async () => {
                  try {
                    await api.post(`/admin/sraivest-banking?bank_name=${encodeURIComponent(bankingForm.bank_name)}&account_holder_name=${encodeURIComponent(bankingForm.account_holder_name)}&routing_number=${bankingForm.routing_number}&account_number=${bankingForm.account_number}&account_type=${bankingForm.account_type}`);
                    toast.success('Banking information saved!');
                    setShowBankingForm(false);
                    fetchDashboard();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to save banking info');
                  }
                }}
              >
                Save Banking Info
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Details Dialog */}
        <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>User Details</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-6 py-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                    {selectedUser.user.picture ? (
                      <img src={selectedUser.user.picture} alt={selectedUser.user.name} className="w-full h-full object-cover" />
                    ) : (
                      <Users className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedUser.user.name}</h3>
                    <p className="text-gray-400">{selectedUser.user.email}</p>
                    <p className="text-sm text-gray-500">ID: {selectedUser.user.user_id}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-white/5">
                    <p className="text-xs text-gray-500">Balance</p>
                    <p className="text-xl font-bold text-[#00E676] number">{formatCurrency(selectedUser.user.balance)}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5">
                    <p className="text-xs text-gray-500">2FA</p>
                    <p className="text-xl font-bold text-white">{selectedUser.user.two_fa_enabled ? 'Enabled' : 'Disabled'}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5">
                    <p className="text-xs text-gray-500">Referral Code</p>
                    <p className="text-xl font-bold text-white">{selectedUser.user.referral_code}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-2">Recent Transactions</h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {selectedUser.recent_transactions.map((txn, i) => (
                      <div key={i} className="flex justify-between p-2 rounded bg-white/5 text-sm">
                        <span className="text-gray-400 capitalize">{txn.type}</span>
                        <span className="text-white number">{formatCurrency(txn.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Edit User</DialogTitle>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={editingUser.name} onChange={(e) => setEditingUser({...editingUser, name: e.target.value})} className="input-dark" data-testid="edit-user-name" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={editingUser.email} onChange={(e) => setEditingUser({...editingUser, email: e.target.value})} className="input-dark" data-testid="edit-user-email" />
                </div>
                <div className="space-y-2">
                  <Label>Balance</Label>
                  <Input type="number" value={editingUser.balance} onChange={(e) => setEditingUser({...editingUser, balance: e.target.value})} className="input-dark" data-testid="edit-user-balance" />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingUser(null)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleUpdateUser} className="btn-primary" data-testid="save-user-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={!!deletingUser} onOpenChange={() => setDeletingUser(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <AlertTriangle className="w-5 h-5" /> Delete User
              </DialogTitle>
            </DialogHeader>
            {deletingUser && (
              <div className="py-4">
                <p className="text-gray-300">Are you sure you want to delete <strong className="text-white">{deletingUser.name}</strong> ({deletingUser.email})?</p>
                <p className="text-sm text-red-400 mt-2">This will permanently delete all their data including portfolio, transactions, and investments.</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeletingUser(null)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-user">Delete User</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Business Dialog */}
        <Dialog open={creatingBusiness} onOpenChange={() => setCreatingBusiness(false)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add New Business</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name *</Label>
                  <Input value={newBusiness.name} onChange={(e) => setNewBusiness({...newBusiness, name: e.target.value})} className="input-dark" data-testid="new-business-name" />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Input value={newBusiness.category} onChange={(e) => setNewBusiness({...newBusiness, category: e.target.value})} className="input-dark" placeholder="e.g., Food & Beverage" data-testid="new-business-category" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <textarea value={newBusiness.description} onChange={(e) => setNewBusiness({...newBusiness, description: e.target.value})} className="input-dark w-full h-24 resize-none p-3 rounded-lg" data-testid="new-business-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Owner Name *</Label>
                  <Input value={newBusiness.owner_name} onChange={(e) => setNewBusiness({...newBusiness, owner_name: e.target.value})} className="input-dark" data-testid="new-business-owner" />
                </div>
                <div className="space-y-2">
                  <Label>Location *</Label>
                  <Input value={newBusiness.location} onChange={(e) => setNewBusiness({...newBusiness, location: e.target.value})} className="input-dark" placeholder="e.g., Austin, TX" data-testid="new-business-location" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Investment Goal ($) *</Label>
                  <Input type="number" value={newBusiness.investment_goal} onChange={(e) => setNewBusiness({...newBusiness, investment_goal: e.target.value})} className="input-dark" data-testid="new-business-goal" />
                </div>
                <div className="space-y-2">
                  <Label>Min Investment ($)</Label>
                  <Input type="number" value={newBusiness.min_investment} onChange={(e) => setNewBusiness({...newBusiness, min_investment: e.target.value})} className="input-dark" data-testid="new-business-min" />
                </div>
                <div className="space-y-2">
                  <Label>Expected Return (%)</Label>
                  <Input type="number" value={newBusiness.expected_return} onChange={(e) => setNewBusiness({...newBusiness, expected_return: e.target.value})} className="input-dark" data-testid="new-business-return" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Image URL</Label>
                <Input value={newBusiness.image_url} onChange={(e) => setNewBusiness({...newBusiness, image_url: e.target.value})} className="input-dark" placeholder="https://..." data-testid="new-business-image" />
              </div>
              <div className="space-y-2">
                <Label>Website URL</Label>
                <Input value={newBusiness.website} onChange={(e) => setNewBusiness({...newBusiness, website: e.target.value})} className="input-dark" placeholder="https://..." data-testid="new-business-website" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={newBusiness.featured} onChange={(e) => setNewBusiness({...newBusiness, featured: e.target.checked})} className="w-4 h-4" id="featured-checkbox" />
                <Label htmlFor="featured-checkbox" className="cursor-pointer">Featured Business</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreatingBusiness(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleCreateBusiness} className="btn-primary" data-testid="save-new-business">Create Business</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Business Dialog */}
        <Dialog open={!!editingBusiness} onOpenChange={() => setEditingBusiness(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Business</DialogTitle>
            </DialogHeader>
            {editingBusiness && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Business Name</Label>
                  <Input value={editingBusiness.name} onChange={(e) => setEditingBusiness({...editingBusiness, name: e.target.value})} className="input-dark" data-testid="edit-business-name" />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input value={editingBusiness.category} onChange={(e) => setEditingBusiness({...editingBusiness, category: e.target.value})} className="input-dark" data-testid="edit-business-category" />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select value={editingBusiness.status} onChange={(e) => setEditingBusiness({...editingBusiness, status: e.target.value})} className="input-dark w-full p-3 rounded-lg bg-white/5 border border-white/10" data-testid="edit-business-status">
                    <option value="active">Active</option>
                    <option value="funded">Funded</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Expected Return (%)</Label>
                  <Input type="number" value={editingBusiness.expected_return} onChange={(e) => setEditingBusiness({...editingBusiness, expected_return: e.target.value})} className="input-dark" data-testid="edit-business-return" />
                </div>
                {/* Show migrate button for mock businesses */}
                {!editingBusiness.hasOwnProperty('_id') && editingBusiness.business_id?.startsWith('biz_') && (
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <p className="text-sm text-yellow-400 mb-2">This is a mock business. Migrate to database for full edit support.</p>
                    <Button onClick={() => { handleMigrateBusiness(editingBusiness.business_id); setEditingBusiness(null); }} className="btn-secondary text-yellow-400">
                      Migrate to Database
                    </Button>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingBusiness(null)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleUpdateBusiness} className="btn-primary" data-testid="save-business-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Business Dialog */}
        <Dialog open={!!deletingBusiness} onOpenChange={() => setDeletingBusiness(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <AlertTriangle className="w-5 h-5" /> Delete Business
              </DialogTitle>
            </DialogHeader>
            {deletingBusiness && (
              <div className="py-4">
                <p className="text-gray-300">Are you sure you want to delete <strong className="text-white">{deletingBusiness.name}</strong>?</p>
                <p className="text-sm text-gray-400 mt-2">This action cannot be undone. Make sure there are no active investments before deleting.</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeletingBusiness(null)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleDeleteBusiness} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-business">Delete Business</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payout Dialog */}
        <Dialog open={!!payoutBusiness} onOpenChange={() => setPayoutBusiness(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Process Payout</DialogTitle>
            </DialogHeader>
            {payoutBusiness && (
              <div className="space-y-4 py-4">
                <div className="p-4 rounded-xl bg-white/5">
                  <h3 className="font-semibold text-white">{payoutBusiness.business_name}</h3>
                  <p className="text-sm text-gray-400">Total Invested: {formatCurrency(payoutBusiness.total_invested)}</p>
                  <p className="text-sm text-gray-400">Investors: {payoutBusiness.investor_count}</p>
                </div>

                <div className="space-y-2">
                  <Label>Payout Amount (Total from Business)</Label>
                  <Input
                    type="number"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                    placeholder="Enter total payout amount"
                    className="input-dark"
                    data-testid="payout-amount-input"
                  />
                  <p className="text-xs text-gray-500">
                    Platform will take 20% from business, 10% from each investor's share
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayoutBusiness(null)} className="btn-secondary">Cancel</Button>
              <Button onClick={processPayout} disabled={processingPayout} className="btn-primary" data-testid="confirm-payout-btn">
                {processingPayout ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Process Payout
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Rejection Dialog */}
        <Dialog open={!!verifyingPayment} onOpenChange={() => { setVerifyingPayment(null); setAdminNotes(''); }}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-500" style={{ fontFamily: 'Outfit, sans-serif' }}>Reject Payment</DialogTitle>
            </DialogHeader>
            {verifyingPayment && (
              <div className="space-y-4 py-4">
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-white font-medium">{verifyingPayment.user_email}</p>
                  <p className="text-gray-400 text-sm">
                    {verifyingPayment.method === 'cashapp' ? 'Cash App' : 'Venmo'}: 
                    <span className="text-white ml-1">{verifyingPayment.cashtag || verifyingPayment.venmo_username}</span>
                  </p>
                  <p className="text-[#00E676] font-bold">${verifyingPayment.gross_amount}</p>
                </div>
                <div className="space-y-2">
                  <Label>Rejection Reason (Required)</Label>
                  <Input
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Enter reason for rejection"
                    className="input-dark"
                    data-testid="rejection-reason-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setVerifyingPayment(null); setAdminNotes(''); }} className="btn-secondary">
                Cancel
              </Button>
              <Button 
                onClick={() => handleRejectPayment(verifyingPayment?.transaction_id)} 
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-reject-payment"
              >
                Reject Payment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
